import React, { useState, useEffect } from "react";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  InputAdornment,
  Tooltip,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { protectedResources } from "../../authConfig";
import { useMsal } from "@azure/msal-react";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  "&:before": {
    display: "none",
  },
  borderRadius: "8px !important",
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: `1px solid ${theme.palette.divider}`,
  "&.Mui-expanded": {
    margin: theme.spacing(2, 0),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  borderRadius: "8px",
  backgroundColor: "#f8f9fa",
  "&.Mui-expanded": {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: "#f0f2f5",
  },
}));

const FuelSurchargeCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  backgroundColor: "#f8f9fa",
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
}));

const FuelSurchargeTable = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(1),
  "& .MuiTableHead-root": {
    backgroundColor: "#1976d2",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: 600,
    },
  },
  "& .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

const FuelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#1976d2",
  color: "white",
  fontWeight: "bold",
  "& .MuiChip-deleteIcon": {
    color: "white",
  },
}));

const BASE_URL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api`;

const getApiUrl = () => {
  return BASE_URL || "http://localhost:3000/api";
};

export default function PriceListManager() {
  const [expandedCarrier, setExpandedCarrier] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPriceList, setEditingPriceList] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [carrierData, setCarrierData] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentCarrierId, setCurrentCarrierId] = useState(null);
  // State עבור היטל דלק DHL
  const [fuelSurcharges, setFuelSurcharges] = useState([]);
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [fuelFormData, setFuelFormData] = useState({
    month: "",
    year: "",
    percentage: "",
  });
  const [editingFuelSurcharge, setEditingFuelSurcharge] = useState(null);
  const [fuelLoading, setFuelLoading] = useState(false);

  const msal = useMsal();

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const { execute: executeWrite } = useFetchWithMsal({
    scopes: [
      ...protectedResources.apiScopes.scopes.read,
      ...protectedResources.apiScopes.scopes.write,
    ],
  });

  const carriers = [
    { id: "ups", name: "UPS" },
    { id: "dhl", name: "DHL" },
    { id: "fedex", name: "FEDEX" },
  ];

  // פונקציה לפורמט תאריך סיום - מטפלת בכל המקרים
  const formatEndDate = (endDate) => {
    // אם אין תאריך סיום או זה ערך שמסמן "ללא הגבלה"
    if (
      !endDate ||
      endDate === "" ||
      endDate === "ללא הגבלה" ||
      endDate === "UNLIMITED" ||
      endDate === "9999-12-31"
    ) {
      return "ללא הגבלה";
    }

    // נסה להמיר לתאריך
    const dateObj = new Date(endDate);

    // בדוק אם התאריך תקין
    if (isNaN(dateObj.getTime())) {
      return "ללא הגבלה";
    }

    // בדוק אם התאריך הוא רחוק מאוד בעתיד (שנה 9999 או יותר)
    if (dateObj.getFullYear() >= 9999) {
      return "ללא הגבלה";
    }

    // אחרת, החזר את התאריך בפורמט עברי
    return dateObj.toLocaleDateString("he-IL");
  };

  // פונקציות לניהול היטל דלק
  const loadFuelSurcharges = async () => {
    try {
      setFuelLoading(true);
      const response = await execute(
        "GET",
        `${getApiUrl()}/carriers/dhl/fuel-surcharges`
      );
      setFuelSurcharges(response.surcharges || []);
    } catch (err) {
      console.error("Error loading fuel surcharges:", err);
      setFuelSurcharges([]);
    } finally {
      setFuelLoading(false);
    }
  };

  const handleSaveFuelSurcharge = async () => {
    if (
      !fuelFormData.month ||
      !fuelFormData.year ||
      fuelFormData.percentage === ""
    ) {
      setError("נא למלא את כל השדות");
      return;
    }

    if (fuelFormData.percentage < 0 || fuelFormData.percentage > 100) {
      setError("אחוז היטל דלק חייב להיות בין 0-100");
      return;
    }

    // בדיקת תאריך לא עתידי מדי
    const currentDate = new Date();
    const selectedDate = new Date(
      parseInt(fuelFormData.year),
      parseInt(fuelFormData.month) - 1
    );
    const maxFutureDate = new Date(
      currentDate.getFullYear() + 1,
      currentDate.getMonth()
    );

    if (selectedDate > maxFutureDate) {
      setError("לא ניתן להוסיף היטל דלק יותר משנה קדימה");
      return;
    }

    try {
      setFuelLoading(true);

      const method = editingFuelSurcharge ? "PUT" : "POST";
      const url = editingFuelSurcharge
        ? `${getApiUrl()}/carriers/dhl/fuel-surcharges/${
            editingFuelSurcharge.id
          }`
        : `${getApiUrl()}/carriers/dhl/fuel-surcharges`;

      const response = await executeWrite(method, url, {
        month: parseInt(fuelFormData.month),
        year: parseInt(fuelFormData.year),
        percentage: parseFloat(fuelFormData.percentage),
      });

      const monthName = getMonthName(parseInt(fuelFormData.month));
      const successMessage = editingFuelSurcharge
        ? `היטל דלק לחודש ${monthName} ${fuelFormData.year} עודכן בהצלחה ל-${fuelFormData.percentage}%`
        : `היטל דלק לחודש ${monthName} ${fuelFormData.year} נוסף בהצלחה (${fuelFormData.percentage}%)`;

      setSuccess(successMessage);
      setShowFuelForm(false);
      setFuelFormData({ month: "", year: "", percentage: "" });
      setEditingFuelSurcharge(null);
      await loadFuelSurcharges();
    } catch (err) {
      console.error("Error saving fuel surcharge:", err);

      if (err.response?.data?.error === "DUPLICATE_ENTRY") {
        setError("כבר קיים היטל דלק עבור החודש והשנה שנבחרו");
      } else if (err.response?.status === 409) {
        setError("כבר קיים היטל דלק עבור התקופה הנבחרת");
      } else {
        setError(err.response?.data?.message || "שגיאה בשמירת היטל הדלק");
      }
    } finally {
      setFuelLoading(false);
    }
  };

  const handleDeleteFuelSurcharge = async (surchargeId) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק את היטל הדלק?")) {
      return;
    }

    try {
      setFuelLoading(true);
      await executeWrite(
        "DELETE",
        `${getApiUrl()}/carriers/dhl/fuel-surcharges/${surchargeId}`
      );

      setSuccess("היטל דלק נמחק בהצלחה");
      await loadFuelSurcharges();
    } catch (err) {
      console.error("Error deleting fuel surcharge:", err);
      setError("שגיאה במחיקת היטל הדלק");
    } finally {
      setFuelLoading(false);
    }
  };

  const handleEditFuelSurcharge = (surcharge) => {
    setEditingFuelSurcharge(surcharge);
    setFuelFormData({
      month: surcharge.month.toString(),
      year: surcharge.year.toString(),
      percentage: surcharge.percentage.toString(),
    });
    setShowFuelForm(true);
  };

  const closeFuelForm = () => {
    setShowFuelForm(false);
    setFuelFormData({ month: "", year: "", percentage: "" });
    setEditingFuelSurcharge(null);
  };

  const getMonthName = (month) => {
    const months = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return months[month - 1];
  };

  const getCurrentYear = () => new Date().getFullYear();
  const getYearRange = () => {
    const currentYear = getCurrentYear();
    return Array.from({ length: currentYear - 2020 + 6 }, (_, i) => 2020 + i);
  };

  const loadCarrierData = async (carrierId) => {
    try {
      const priceLists = await execute(
        "GET",
        `${getApiUrl()}/carriers/${carrierId}/price-lists`
      );
      setCarrierData((prev) => ({
        ...prev,
        [carrierId]: priceLists,
      }));

      if (carrierId === "dhl") {
        await loadFuelSurcharges();
      }
    } catch (err) {
      console.error("Error loading price lists:", err);
      const errorMessage =
        err.response?.status === 404
          ? "לא נמצא שירות API למחירונים"
          : err.message || "שגיאה בטעינת המחירונים";
      setError(errorMessage);
    }
  };

  useEffect(() => {
    const loadAllCarriers = async () => {
      setLoading(true);
      try {
        await Promise.all(
          carriers.map((carrier) => loadCarrierData(carrier.id))
        );
      } finally {
        setLoading(false);
      }
    };
    loadAllCarriers();
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/tiff",
      ];

      if (!validTypes.includes(file.type)) {
        setError("סוג קובץ לא תקין. יש להעלות קובץ Excel, CSV, PDF או תמונה.");
        return;
      }

      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        setError("הקובץ גדול מדי. גודל מקסימלי הוא 20MB.");
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (event, carrierId) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!startDate) {
      setError("נא להזין תאריך התחלה");
      return;
    }

    const startDateObj = new Date(startDate);
    if (isNaN(startDateObj.getTime())) {
      setError("תאריך ההתחלה אינו תקין");
      return;
    }

    // 🔥 תיקון מרכזי - קביעת ערך ברור לשליחה
    let endDateToSend;

    if (endDate && endDate.trim() !== "") {
      const endDateObj = new Date(endDate);

      if (isNaN(endDateObj.getTime())) {
        setError("תאריך הסיום אינו תקין");
        return;
      }

      if (endDateObj <= startDateObj) {
        setError("תאריך הסיום חייב להיות מאוחר מתאריך ההתחלה");
        return;
      }

      endDateToSend = endDate; // תאריך תקין
    } else {
      // 🔥 כשאין תאריך סיום - שלח מחרוזת ריקה במפורש
      endDateToSend = "";
    }

    if (!editingPriceList && !selectedFile) {
      setError("נא לבחור קובץ");
      return;
    }

    try {
      setLoading(true);

      const url = editingPriceList
        ? `${getApiUrl()}/carriers/${carrierId}/price-lists/${
            editingPriceList.id
          }`
        : `${getApiUrl()}/carriers/${carrierId}/price-lists`;

      const method = editingPriceList ? "PATCH" : "POST";

      const formData = new FormData();
      if (selectedFile) formData.append("file", selectedFile);
      formData.append("startDate", startDate);

      // 🔥 תמיד שלח את endDate - אם ריק שלח "UNLIMITED" כסימון
      if (endDateToSend === "") {
        formData.append("endDate", "UNLIMITED");
      } else {
        formData.append("endDate", endDateToSend);
      }

      formData.append("updateExisting", "true");

      console.log("📤 Sending to server:", {
        startDate,
        endDate: endDateToSend === "" ? "UNLIMITED (ללא הגבלה)" : endDateToSend,
        hasFile: !!selectedFile,
        method,
      });

      const response = await executeWrite(method, url, formData);

      let successMessage = "המחירון נשמר בהצלחה!";

      if (
        response &&
        response.data &&
        response.data.updatedPriceLists &&
        response.data.updatedPriceLists.length > 0
      ) {
        const updatesList = response.data.updatedPriceLists
          .map((update) => {
            const oldEndDateStr = update.oldEndDate || "ללא הגבלה";
            const newEndDateStr = update.newEndDate;
            return `המחירון "${update.fileName}" (${formatDate(
              update.oldStartDate
            )} - ${oldEndDateStr}) עודכן לסיום בתאריך ${formatDate(
              newEndDateStr
            )}`;
          })
          .join("\n");

        successMessage += `\n\nשינויים שבוצעו:\n${updatesList}`;
      }

      setSuccess(successMessage);
      handleCloseForm();
      await loadCarrierData(carrierId);
    } catch (err) {
      console.error("Error uploading price list:", err);

      if (err.status === 409 || err.response?.data?.error === "DATE_CONFLICT") {
        const errorMsg =
          err.msg ||
          err.response?.data?.message ||
          "קיימת חפיפת תאריכים עם מחירונים קיימים";
        setError(
          `לא ניתן להוסיף את המחירון - ${errorMsg}. אנא בחר תאריך התחלה אחר.`
        );
      } else if (err.status === 400 || err.response?.status === 400) {
        setError(
          err.msg ||
            err.response?.data?.message ||
            "נתוני התאריכים שהוזנו אינם תקינים"
        );
      } else {
        setError(err.message || err.msg || "שגיאה בעיבוד המחירון");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (carrierId, fileName) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק את המחירון?")) {
      return;
    }

    try {
      setLoading(true);
      await executeWrite(
        "DELETE",
        `${getApiUrl()}/carriers/${carrierId}/price-lists/${fileName}`
      );
      await loadCarrierData(carrierId);
      setSuccess("המחירון נמחק בהצלחה");
    } catch (err) {
      console.error("Error deleting price list:", err);
      setError(err.message || "שגיאה במחיקת המחירון");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (carrierId, fileName) => {
    try {
      setLoading(true);

      const downloadUrl = `${getApiUrl()}/carriers/${carrierId}/price-lists/${fileName}/download`;

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${await getMsalToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`שגיאה בהורדת הקובץ: ${response.statusText}`);
      }

      const blob = await response.blob();

      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = fileName;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      URL.revokeObjectURL(downloadLink.href);

      setSuccess("המחירון הורד בהצלחה");
    } catch (err) {
      console.error("שגיאה בהורדת המחירון:", err);
      setError(err.message || "שגיאה בהורדת הקובץ");
    } finally {
      setLoading(false);
    }
  };

  const getMsalToken = async () => {
    const { instance, accounts } = msal;

    if (accounts.length === 0) {
      throw new Error("User not logged in");
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: protectedResources.apiScopes.scopes.read,
        account: accounts[0],
      });

      return tokenResponse.accessToken;
    } catch (error) {
      console.error("Error acquiring token", error);
      throw error;
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingPriceList(null);
    setSelectedFile(null);
    setStartDate("");
    setEndDate("");
    setError(null);
    setCurrentCarrierId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL");
  };

  const handleStartDateChange = (newDate) => {
    setStartDate(newDate);

    if (endDate) {
      const start = new Date(newDate);
      const end = new Date(endDate);

      if (end <= start) {
        setError("תאריך סיום חייב להיות מאוחר יותר מתאריך ההתחלה");
      } else {
        setError(null);
      }
    }
  };

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);

    // 🔥 תיקון פונקציית האימות
    if (startDate && newDate && newDate.trim() !== "") {
      const start = new Date(startDate);
      const end = new Date(newDate);

      if (end <= start) {
        setError("תאריך סיום חייב להיות מאוחר יותר מתאריך ההתחלה");
      } else {
        setError(null);
      }
    } else {
      // אם השדה ריק - זה בסדר (ללא הגבלה)
      setError(null);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            ניהול מחירונים
          </Typography>

          {carriers.map((carrier) => (
            <StyledAccordion
              key={carrier.id}
              expanded={expandedCarrier === carrier.id}
              onChange={() =>
                setExpandedCarrier(
                  expandedCarrier === carrier.id ? false : carrier.id
                )
              }
            >
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="h6">{carrier.name}</Typography>
                  <Chip
                    label={`${carrierData[carrier.id]?.length || 0} מחירונים`}
                    size="small"
                    sx={{ backgroundColor: "rgba(0,0,0,0.08)" }}
                  />
                  {carrier.id === "dhl" && fuelSurcharges.length > 0 && (
                    <Chip
                      icon={<LocalGasStationIcon />}
                      label={`${fuelSurcharges.length} היטלי דלק`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </StyledAccordionSummary>

              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={() => {
                      setCurrentCarrierId(carrier.id);
                      setEditingPriceList(null);
                      setShowForm(true);
                    }}
                  >
                    הוסף מחירון חדש
                  </Button>
                </Box>

                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>שם קובץ</TableCell>
                        <TableCell>תאריך תחילה</TableCell>
                        <TableCell>תאריך סיום</TableCell>
                        <TableCell>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {carrierData[carrier.id]?.map((priceList) => (
                        <TableRow key={priceList.id}>
                          <TableCell>{priceList.fileName}</TableCell>
                          <TableCell>
                            {new Date(priceList.startDate).toLocaleDateString(
                              "he-IL"
                            )}
                          </TableCell>
                          <TableCell>
                            {formatEndDate(priceList.endDate)}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              <Tooltip title="ערוך מחירון">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setCurrentCarrierId(carrier.id);
                                    setEditingPriceList(priceList);
                                    setStartDate(priceList.startDate);

                                    // טיפול מושכל בתאריך סיום
                                    const endDateValue = priceList.endDate;

                                    if (
                                      !endDateValue ||
                                      endDateValue === "ללא הגבלה" ||
                                      endDateValue === "UNLIMITED" ||
                                      endDateValue === "9999-12-31"
                                    ) {
                                      // אין תאריך סיום - השאר ריק
                                      setEndDate("");
                                    } else {
                                      // נסה להמיר לתאריך תקין
                                      const endDateObj = new Date(endDateValue);

                                      if (
                                        !isNaN(endDateObj.getTime()) &&
                                        endDateObj.getFullYear() < 9999
                                      ) {
                                        // תאריך תקין - המר לפורמט input
                                        setEndDate(
                                          endDateObj.toISOString().split("T")[0]
                                        );
                                      } else {
                                        // תאריך לא תקין - השאר ריק
                                        setEndDate("");
                                      }
                                    }

                                    setShowForm(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="הורד מחירון">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDownload(carrier.id, priceList.id)
                                  }
                                  disabled={loading}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="מחק מחירון">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDelete(carrier.id, priceList.id)
                                  }
                                  disabled={loading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* חלק היטל דלק עבור DHL */}
                {carrier.id === "dhl" && (
                  <FuelSurchargeCard>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 3,
                        }}
                      >
                        <LocalGasStationIcon
                          sx={{
                            fontSize: 30,
                            backgroundColor: "#1976d2",
                            borderRadius: "50%",
                            padding: 1,
                            color: "white",
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{ fontWeight: "bold" }}
                          >
                            ניהול היטל דלק DHL
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoIcon fontSize="small" />
                            נתונים מתעדכנים מאתר DHL:
                            <Link
                              href="https://mydhl.express.dhl/il/he/ship/surcharges.html#/fuel_surcharge"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ ml: 0.5 }}
                            >
                              קישור לאתר DHL
                            </Link>
                          </Typography>
                        </Box>
                        <Tooltip title="הוסף היטל דלק חדש או עדכן קיים">
                          <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            size="medium"
                            onClick={() => setShowFuelForm(true)}
                            disabled={fuelLoading}
                            sx={{
                              backgroundColor: "#1976d2",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            }}
                          >
                            הוסף היטל דלק
                          </Button>
                        </Tooltip>
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      {fuelLoading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                          <CircularProgress size={32} />
                        </Box>
                      ) : fuelSurcharges.length > 0 ? (
                        <Box>
                          <Alert
                            severity="info"
                            sx={{
                              mb: 2,
                              backgroundColor: "#e3f2fd",
                            }}
                            icon={<TrendingUpIcon />}
                          >
                            <Typography variant="body2">
                              <strong>עדכון אחרון:</strong>{" "}
                              {new Date(
                                fuelSurcharges[0]?.updatedAt
                              ).toLocaleDateString("he-IL")}{" "}
                              |<strong> סה"כ רשומות:</strong>{" "}
                              {fuelSurcharges.length}
                            </Typography>
                          </Alert>

                          <FuelSurchargeTable component={Paper} elevation={3}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: "bold" }}>
                                    תקופה
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: "bold" }}>
                                    אחוז היטל
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: "bold" }}>
                                    עדכון אחרון
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: "bold" }}>
                                    פעולות
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {fuelSurcharges.map((surcharge, index) => (
                                  <TableRow key={surcharge.id} hover>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <FuelChip
                                          label={`${getMonthName(
                                            surcharge.month
                                          )} ${surcharge.year}`}
                                          size="small"
                                        />
                                        {index === 0 && (
                                          <Chip
                                            label="נוכחי"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            color: "primary.main",
                                            fontWeight: "bold",
                                            fontSize: "1.1rem",
                                          }}
                                        >
                                          {surcharge.percentage}%
                                        </Typography>
                                        {index > 0 &&
                                          fuelSurcharges[index - 1] && (
                                            <Tooltip
                                              title={`שינוי מהחודש הקודם: ${(
                                                surcharge.percentage -
                                                fuelSurcharges[index - 1]
                                                  .percentage
                                              ).toFixed(2)}%`}
                                            >
                                              <Chip
                                                label={
                                                  surcharge.percentage >
                                                  fuelSurcharges[index - 1]
                                                    .percentage
                                                    ? `+${(
                                                        surcharge.percentage -
                                                        fuelSurcharges[
                                                          index - 1
                                                        ].percentage
                                                      ).toFixed(2)}%`
                                                    : `${(
                                                        surcharge.percentage -
                                                        fuelSurcharges[
                                                          index - 1
                                                        ].percentage
                                                      ).toFixed(2)}%`
                                                }
                                                size="small"
                                                color={
                                                  surcharge.percentage >
                                                  fuelSurcharges[index - 1]
                                                    .percentage
                                                    ? "error"
                                                    : "success"
                                                }
                                                variant="outlined"
                                              />
                                            </Tooltip>
                                          )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {new Date(
                                          surcharge.updatedAt
                                        ).toLocaleDateString("he-IL", {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <Tooltip title="ערוך היטל דלק">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleEditFuelSurcharge(surcharge)
                                            }
                                            disabled={fuelLoading}
                                            sx={{
                                              color: "primary.main",
                                              "&:hover": {
                                                backgroundColor:
                                                  "primary.light",
                                                color: "white",
                                              },
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="מחק היטל דלק">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDeleteFuelSurcharge(
                                                surcharge.id
                                              )
                                            }
                                            disabled={fuelLoading}
                                            sx={{
                                              color: "error.main",
                                              "&:hover": {
                                                backgroundColor: "error.light",
                                                color: "white",
                                              },
                                            }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </FuelSurchargeTable>
                        </Box>
                      ) : (
                        <Alert
                          severity="info"
                          sx={{
                            mt: 2,
                            backgroundColor: "#e3f2fd",
                          }}
                          icon={<InfoIcon />}
                        >
                          <Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <strong>לא נמצאו נתוני היטל דלק.</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              נתוני היטל הדלק של DHL מתעדכנים מדי חודש. בדוק את
                              הערכים העדכניים באתר DHL הרשמי והוסף אותם כאן.
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 1 }}
                            >
                              💡 טיפ: ערכי היטל הדלק משמשים לחישוב עלויות משלוח
                              מדויקות יותר
                            </Typography>
                          </Box>
                        </Alert>
                      )}
                    </CardContent>
                  </FuelSurchargeCard>
                )}
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </CardContent>
      </Card>

      {/* Dialog להוספת/עריכת מחירון */}
      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPriceList ? "עריכת מחירון" : "הוספת מחירון חדש"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate sx={{ mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              {editingPriceList ? (
                <Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    קובץ נוכחי: {editingPriceList.fileName}
                  </Typography>
                  <input
                    style={{ display: "none" }}
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                    >
                      {selectedFile
                        ? selectedFile.name
                        : "החלף קובץ (Excel, CSV או PDF)"}
                    </Button>
                  </label>
                </Box>
              ) : (
                <>
                  <input
                    style={{ display: "none" }}
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                    >
                      {selectedFile
                        ? selectedFile.name
                        : "בחר קובץ מחירון (Excel, CSV או PDF)"}
                    </Button>
                  </label>
                </>
              )}
            </Box>

            <TextField
              fullWidth
              required
              label="תאריך תחילת תוקף"
              type="date"
              value={startDate || ""}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
              error={!!error && error.includes("תאריך")}
            />

            <TextField
              fullWidth
              label="תאריך סיום תוקף (אופציונלי)"
              type="date"
              value={endDate || ""}
              onChange={(e) => handleEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
              error={!!error && error.includes("תאריך סיום")}
              helperText={
                startDate &&
                endDate &&
                endDate.trim() !== "" &&
                new Date(endDate) <= new Date(startDate)
                  ? "תאריך הסיום חייב להיות מאוחר יותר מתאריך ההתחלה"
                  : "השאר ריק לתוקף ללא הגבלה"
              }
            />

            {endDate &&
              endDate.trim() !== "" &&
              new Date(endDate) <= new Date(startDate) && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2">
                    שים לב: תאריך הסיום ({endDate}) מוקדם מתאריך ההתחלה (
                    {startDate}). תאריך הסיום חייב להיות מאוחר יותר מתאריך
                    ההתחלה.
                  </Typography>
                </Alert>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>ביטול</Button>
          <Button
            variant="contained"
            onClick={(e) => handleSubmit(e, currentCarrierId)} // ✅ השתמשי ב-currentCarrierId
            disabled={!currentCarrierId} // ✅ מנעי שליחה אם אין carrierId
          >
            {editingPriceList ? "עדכן" : "הוסף"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog להוספת/עריכת היטל דלק */}
      <Dialog
        open={showFuelForm}
        onClose={closeFuelForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#1976d2",
            color: "white",
          }}
        >
          <LocalGasStationIcon />
          {editingFuelSurcharge ? "עריכת היטל דלק" : "הוספת היטל דלק חדש"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>חודש</InputLabel>
                <Select
                  value={fuelFormData.month}
                  label="חודש"
                  onChange={(e) =>
                    setFuelFormData((prev) => ({
                      ...prev,
                      month: e.target.value,
                    }))
                  }
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {getMonthName(i + 1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>שנה</InputLabel>
                <Select
                  value={fuelFormData.year}
                  label="שנה"
                  onChange={(e) =>
                    setFuelFormData((prev) => ({
                      ...prev,
                      year: e.target.value,
                    }))
                  }
                >
                  {getYearRange().map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="אחוז היטל דלק"
                type="number"
                value={fuelFormData.percentage}
                onChange={(e) =>
                  setFuelFormData((prev) => ({
                    ...prev,
                    percentage: e.target.value,
                  }))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                    max: 100,
                    step: 0.01,
                    style: { textAlign: "center" },
                  },
                }}
                helperText="הזן אחוז בין 0-100 (עד 2 ספרות אחרי הנקודה)"
                error={
                  !!fuelFormData.percentage &&
                  (parseFloat(fuelFormData.percentage) < 0 ||
                    parseFloat(fuelFormData.percentage) > 100)
                }
                sx={{
                  "& .MuiOutlinedInput-input": {
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                  },
                }}
              />
              {fuelFormData.percentage && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{
                    display: "block",
                    mt: 1,
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                ></Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={closeFuelForm}
            startIcon={<CancelIcon />}
            variant="outlined"
          >
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveFuelSurcharge}
            disabled={fuelLoading}
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {editingFuelSurcharge ? "עדכן" : "הוסף"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {successMessage && (
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage(null)}
          sx={{
            mt: 2,
            fontWeight: 500,
            bgcolor: "#4caf50",
            "& .MuiAlert-message": { textAlign: "right" },
          }}
        >
          <Typography
            variant="body1"
            dangerouslySetInnerHTML={{ __html: successMessage }}
          />
        </Alert>
      )}
    </Box>
  );
}
